import React, { useState } from 'react';
import { OpportunityItem, OpportunityStatus, NEXT_STATUS, STATUS_COLOR, STATUS_SEQUENCE, LANE, LANE_COLOR } from '../models/opportunity';

interface Props {
  item: OpportunityItem;
  currentUserEmail: string;
  currentUserName: string;
  isMyComercial: boolean;
  isTecnicoPhase: boolean;
  onClose: () => void;
  onAdvanceStatus: (id: string, newStatus: OpportunityStatus, extraFields?: Record<string, any>) => Promise<void>;
  onUpdateExecution: (id: string, progresso: number, entrada: string, diarioActual: string) => Promise<void>;
}

const Row: React.FC<{ label: string; value?: string | number; accent?: string }> = ({ label, value, accent }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
    <span style={{ fontSize: 12, color: 'var(--chk-muted)', minWidth: 150 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: accent || 'var(--chk-text)', textAlign: 'right' }}>{value ?? '—'}</span>
  </div>
);

// Progress stepper showing all N1-N9 phases
const PhaseStepper: React.FC<{ current: OpportunityStatus }> = ({ current }) => {
  const idx = STATUS_SEQUENCE.indexOf(current);
  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 600, gap: 0 }}>
        {STATUS_SEQUENCE.map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          const lane = LANE[s];
          const color = done ? '#22C55E' : active ? STATUS_COLOR[s] : '#2A3050';
          const nLabel = `N${i + 1}`;
          return (
            <React.Fragment key={s}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1, minWidth: 60 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: active ? color : done ? '#22C55E' : '#1A1F35',
                  border: `2px solid ${color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, color: active || done ? '#fff' : '#555',
                  transition: 'all .2s',
                }}>{done ? '✓' : nLabel}</div>
                <div style={{ fontSize: 9, color: active ? STATUS_COLOR[s] : done ? '#4ADE80' : '#3A3F55', textAlign: 'center', lineHeight: 1.2, maxWidth: 58 }}>
                  {s.replace(/^\d+\. /, '')}
                </div>
                <div style={{ fontSize: 8, color: LANE_COLOR[lane], opacity: 0.7 }}>{lane}</div>
              </div>
              {i < STATUS_SEQUENCE.length - 1 && (
                <div style={{ height: 2, flex: 0, width: 8, background: i < idx ? '#22C55E' : '#2A3050', marginBottom: 20 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export const OpportunityDetailModal: React.FC<Props> = ({ item, currentUserEmail, currentUserName, isMyComercial, isTecnicoPhase, onClose, onAdvanceStatus, onUpdateExecution }) => {
  const [tab, setTab] = useState<'info' | 'tecnico' | 'execucao' | 'historico'>('info');
  const [advancing, setAdvancing] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<OpportunityStatus | null>(null);
  const [otNumber, setOtNumber] = useState(item.numeroOT || '');
  const [motivoPerda, setMotivoPerda] = useState('');
  // Execução
  const [progresso, setProgresso] = useState(item.progressoExecucao ?? 0);
  const [entradaDiario, setEntradaDiario] = useState('');
  const [savingExec, setSavingExec] = useState(false);

  const isComercial = item.comercialEmail?.toLowerCase() === currentUserEmail.toLowerCase();
  const isTecnico = item.responsavelTecnicoEmail?.toLowerCase() === currentUserEmail.toLowerCase();
  const nextStatus = NEXT_STATUS[item.statusOportunidade];
  const isTerminal = item.statusOportunidade === '10. Concluído / Ganho' || item.statusOportunidade === '11. Perdido / Cancelado';
  const isExecution = item.statusOportunidade === '8. Em Execução';
  const historico = (item.historicoParecer || '').split('\n\n---\n\n').filter(e => e.trim());
  const diario = (item.diarioBordo || '').split('\n\n---\n\n').filter(e => e.trim());

  const handleAdvance = async () => {
    if (!confirmStatus) return;
    setAdvancing(true);
    try {
      const extra: Record<string, any> = {};
      if (confirmStatus === '7. Ordem de Trabalho Gerada' && otNumber) extra.NumeroOT = otNumber;
      if (confirmStatus === '11. Perdido / Cancelado' && motivoPerda) extra.MotivoPerda = motivoPerda;
      await onAdvanceStatus(item.id, confirmStatus, extra);
      onClose();
    } finally {
      setAdvancing(false);
      setConfirmStatus(null);
    }
  };

  const handleSaveExec = async () => {
    setSavingExec(true);
    try {
      await onUpdateExecution(item.id, progresso, entradaDiario, item.diarioBordo || '');
      setEntradaDiario('');
      onClose();
    } finally {
      setSavingExec(false);
    }
  };

  const tabStyle = (t: string): React.CSSProperties => ({
    padding: '10px 16px', fontSize: 12, fontWeight: 500, cursor: 'pointer', border: 'none',
    background: 'none', color: tab === t ? 'var(--chk-white)' : 'var(--chk-muted)',
    borderBottom: `2px solid ${tab === t ? 'var(--chk-red)' : 'transparent'}`,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--chk-card)', border: '1px solid var(--chk-border)', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--chk-border)', background: 'var(--chk-navy)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--chk-white)' }}>{item.codigoOportunidade}</div>
              <div style={{ fontSize: 13, color: 'var(--chk-muted)', marginTop: 2 }}>🏢 {item.cliente}</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--chk-muted)', fontSize: 22, cursor: 'pointer' }}>✕</button>
          </div>
          {/* Status badge */}
          <div style={{ marginTop: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: `${STATUS_COLOR[item.statusOportunidade]}22`,
              color: STATUS_COLOR[item.statusOportunidade],
              border: `1px solid ${STATUS_COLOR[item.statusOportunidade]}44`,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_COLOR[item.statusOportunidade], display: 'inline-block' }} />
              {item.statusOportunidade} &nbsp;·&nbsp; {LANE[item.statusOportunidade]}
            </span>
          </div>
          {/* Phase stepper */}
          {!isTerminal && (
            <div style={{ marginTop: 14 }}>
              <PhaseStepper current={item.statusOportunidade} />
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--chk-border)', background: 'var(--chk-navy)', padding: '0 4px' }}>
          <button style={tabStyle('info')} onClick={() => setTab('info')}>📋 Informação</button>
          <button style={tabStyle('tecnico')} onClick={() => setTab('tecnico')}>🔧 Técnico</button>
          {(isExecution || item.progressoExecucao != null) && (
            <button style={tabStyle('execucao')} onClick={() => setTab('execucao')}>⚙️ Execução</button>
          )}
          {historico.length > 0 && (
            <button style={tabStyle('historico')} onClick={() => setTab('historico')}>🕐 Histórico ({historico.length})</button>
          )}
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '18px 20px' }}>

          {/* TAB: INFO */}
          {tab === 'info' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--chk-red)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Comercial</div>
                <Row label="Comercial" value={item.comercialNome} />
                <Row label="Email" value={item.comercialEmail} />
                <Row label="Cliente" value={item.cliente} />
                <Row label="Contacto Cliente" value={item.contactoCliente} />
                {item.numeroOT && <Row label="Número OT" value={item.numeroOT} accent="#34D399" />}
                {item.dataAceitacao && <Row label="Data Aceitação" value={item.dataAceitacao} accent="#22C55E" />}
                {item.motivoPerda && <Row label="Motivo Perda" value={item.motivoPerda} accent="#EF4444" />}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--chk-blue)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Âmbito / Necessidades</div>
                <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--chk-text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {item.ambitoNecessidades || '—'}
                </div>
              </div>
              {item.termoAceite && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#22C55E', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Termo de Aceite</div>
                  <div style={{ background: 'rgba(34,197,94,.05)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#4ADE80' }}>{item.termoAceite}</div>
                </div>
              )}
            </div>
          )}

          {/* TAB: TÉCNICO */}
          {tab === 'tecnico' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#22C55E', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Avaliação Técnica</div>
                <Row label="Técnico DTI" value={item.responsavelTecnicoNome} />
                <Row label="Email" value={item.responsavelTecnicoEmail} />
                <Row label="Status Técnico" value={item.statusTecnico} />
                <Row label="Data Limite Parecer" value={item.dataLimiteParecer} />
              </div>
              {item.parecerTecnico && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--chk-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Último Parecer</div>
                  <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--chk-text)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{item.parecerTecnico}</div>
                </div>
              )}
            </div>
          )}

          {/* TAB: EXECUÇÃO */}
          {tab === 'execucao' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Progresso */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1755B8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Progresso de Execução</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <div style={{ flex: 1, height: 8, background: '#1A1F35', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progresso}%`, background: progresso >= 100 ? '#22C55E' : '#1755B8', borderRadius: 4, transition: 'width .3s' }} />
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--chk-white)', minWidth: 40 }}>{progresso}%</span>
                </div>
                {isTecnico && (
                  <input type="range" min={0} max={100} value={progresso} onChange={e => setProgresso(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#1755B8' }} />
                )}
              </div>
              {/* Nova entrada diário */}
              {isTecnico && (
                <div>
                  <label style={{ fontSize: 11, color: 'var(--chk-muted)', display: 'block', marginBottom: 6 }}>NOVA ENTRADA NO DIÁRIO DE BORDO</label>
                  <textarea value={entradaDiario} onChange={e => setEntradaDiario(e.target.value)} rows={4}
                    placeholder="Descreve o trabalho realizado, marcos atingidos, obstáculos..."
                    style={{ width: '100%', background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '10px 12px', color: 'var(--chk-text)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                  <button onClick={handleSaveExec} disabled={savingExec || !entradaDiario.trim()}
                    style={{ marginTop: 8, background: savingExec ? '#555' : 'linear-gradient(135deg,#1755B8,#0F3F8C)', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {savingExec ? '⏳ A gravar...' : '💾 Gravar Progresso'}
                  </button>
                </div>
              )}
              {/* Histórico diário */}
              {diario.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--chk-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Diário de Bordo</div>
                  {diario.map((e, i) => (
                    <div key={i} style={{ background: 'rgba(255,255,255,.03)', borderLeft: '3px solid #1755B8', padding: '10px 12px', borderRadius: 6, marginBottom: 8, fontSize: 12, whiteSpace: 'pre-wrap', color: 'var(--chk-text)', lineHeight: 1.5 }}>{e}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: HISTÓRICO */}
          {tab === 'historico' && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#60A5FA', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Histórico de Pareceres</div>
              {historico.map((entry, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,.03)', borderLeft: '3px solid #1755B8', padding: '10px 12px', borderRadius: 6, marginBottom: 8, fontSize: 12, whiteSpace: 'pre-wrap', color: 'var(--chk-text)', lineHeight: 1.5 }}>{entry}</div>
              ))}
            </div>
          )}
        </div>

        {/* Footer — Avançar Status — só visível para Comercial nas fases comercial */}
        {!isTerminal && isMyComercial && !isTecnicoPhase && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid var(--chk-border)', background: 'rgba(0,0,0,.2)' }}>
            {confirmStatus ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* OT number input */}
                {confirmStatus === '7. Ordem de Trabalho Gerada' && (
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--chk-muted)', display: 'block', marginBottom: 4 }}>NÚMERO DA ORDEM DE TRABALHO</label>
                    <input value={otNumber} onChange={e => setOtNumber(e.target.value)} placeholder="OT-2026-001"
                      style={{ width: '100%', background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '8px 12px', color: 'var(--chk-text)', fontSize: 13 }} />
                  </div>
                )}
                {/* Motivo perda */}
                {confirmStatus === '11. Perdido / Cancelado' && (
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--chk-muted)', display: 'block', marginBottom: 4 }}>MOTIVO DA PERDA / CANCELAMENTO</label>
                    <textarea value={motivoPerda} onChange={e => setMotivoPerda(e.target.value)} rows={2} placeholder="Ex: Orçamento fora do alcance do cliente..."
                      style={{ width: '100%', background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '8px 12px', color: 'var(--chk-text)', fontSize: 13, fontFamily: 'inherit', resize: 'none' }} />
                  </div>
                )}
                <div style={{ fontSize: 13, color: 'var(--chk-text)' }}>
                  Confirmar avanço para <b style={{ color: STATUS_COLOR[confirmStatus] }}>{confirmStatus}</b>?
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setConfirmStatus(null)} style={{ flex: 1, background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', color: 'var(--chk-muted)', borderRadius: 8, padding: '9px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                  <button onClick={handleAdvance} disabled={advancing} style={{ flex: 2, background: `linear-gradient(135deg,${STATUS_COLOR[confirmStatus]},${STATUS_COLOR[confirmStatus]}99)`, color: 'white', border: 'none', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 600, cursor: advancing ? 'not-allowed' : 'pointer' }}>
                    {advancing ? '⏳ A avançar...' : `✅ Confirmar`}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {nextStatus && (
                  <button onClick={() => setConfirmStatus(nextStatus)}
                    style={{ flex: 1, minWidth: 200, background: `linear-gradient(135deg,${STATUS_COLOR[nextStatus]},${STATUS_COLOR[nextStatus]}99)`, color: 'white', border: 'none', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    ➡️ Avançar: {nextStatus.replace(/^\d+\. /, '')}
                  </button>
                )}
                <button onClick={() => setConfirmStatus('11. Perdido / Cancelado')}
                  style={{ background: 'rgba(239,68,68,.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  ❌ Perdido
                </button>
              </div>
            )}
          </div>
        )}

        {/* Terminal states */}
        {item.statusOportunidade === '10. Concluído / Ganho' && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid var(--chk-border)', background: 'rgba(34,197,94,.08)', textAlign: 'center', color: '#22C55E', fontSize: 14, fontWeight: 600 }}>
            🏆 Projeto Concluído com Sucesso!
          </div>
        )}
        {item.statusOportunidade === '11. Perdido / Cancelado' && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid var(--chk-border)', background: 'rgba(239,68,68,.08)', textAlign: 'center', color: '#EF4444', fontSize: 14, fontWeight: 600 }}>
            ❌ Oportunidade Perdida / Cancelada
          </div>
        )}
      </div>
    </div>
  );
};
